import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  User, Phone, Mail, FileText, Loader2, CheckCircle,
  MessageSquare, Calendar, Search, Receipt, Link2, CreditCard,
  UserPlus, GitBranch, Briefcase, X, MapPin, Globe, Clock,
  DollarSign, ExternalLink, ChevronRight
} from 'lucide-react';
import { Button } from '../ui/button';
import { cn } from '../../lib/utils';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const getAuthHeaders = () => ({
  'Authorization': `Bearer ${localStorage.getItem('token')}`,
  'Content-Type': 'application/json'
});

// ═══════════════════════════════════════════════════════════════
// SHARED COMPONENTS
// ═══════════════════════════════════════════════════════════════

const WizardModal = ({ isOpen, onClose, title, subtitle, icon: Icon, iconColor, children, footer, size = 'default' }) => {
  if (!isOpen) return null;
  
  const sizeClasses = {
    default: 'max-w-lg',
    large: 'max-w-2xl',
  };
  
  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-start justify-center p-4 pt-24 pb-8 overflow-y-auto" onClick={onClose}>
      <div
        onClick={e => e.stopPropagation()}
        className={cn(
          "bg-[#12121a] rounded-2xl w-full border border-white/10 overflow-hidden max-h-[calc(100vh-8rem)] flex flex-col my-auto",
          sizeClasses[size]
        )}
      >
        <div className="px-6 py-4 border-b border-white/10 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={cn("w-10 h-10 rounded-xl flex items-center justify-center", iconColor || "bg-primary/20")}>
              <Icon className={cn("w-5 h-5", iconColor ? iconColor.replace('bg-', 'text-').replace('/20', '-400') : "text-primary")} />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-white">{title}</h2>
              <p className="text-sm text-gray-400">{subtitle}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-lg transition-colors text-gray-400">✕</button>
        </div>
        <div className="flex-1 overflow-y-auto p-6 space-y-4">{children}</div>
        <div className="px-6 py-4 border-t border-white/10 flex justify-between items-center sticky bottom-0 bg-[#12121a]">{footer}</div>
      </div>
    </div>
  );
};

const FormField = ({ label, required, children, hint }) => (
  <div>
    <label className="block text-sm font-medium text-gray-300 mb-2">
      {label} {required && <span className="text-red-400">*</span>}
    </label>
    {children}
    {hint && <p className="text-xs text-gray-500 mt-1">{hint}</p>}
  </div>
);

const TextInput = ({ icon: Icon, value, onChange, placeholder, type = 'text', ...props }) => (
  <div className="relative">
    {Icon && <Icon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />}
    <input
      type={type}
      value={value}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
      className={cn(
        "w-full py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-primary/50",
        Icon ? "pl-10 pr-4" : "px-4"
      )}
      {...props}
    />
  </div>
);

const TextArea = ({ value, onChange, placeholder, rows = 3, ...props }) => (
  <textarea
    value={value}
    onChange={e => onChange(e.target.value)}
    placeholder={placeholder}
    rows={rows}
    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-primary/50 resize-none"
    {...props}
  />
);

const SelectInput = ({ value, onChange, options, placeholder, ...props }) => (
  <select
    value={value}
    onChange={e => onChange(e.target.value)}
    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white focus:outline-none focus:border-primary/50"
    {...props}
  >
    {placeholder && <option value="">{placeholder}</option>}
    {options.map(opt => (
      <option key={opt.value || opt} value={opt.value || opt}>
        {opt.label || opt}
      </option>
    ))}
  </select>
);

// Lead selector component (reused in all lead wizards)
const LeadSelector = ({ selectedLead, onSelect, label = "Select Lead" }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  
  const searchLeads = async () => {
    if (!searchQuery.trim()) return;
    
    setSearching(true);
    try {
      const response = await fetch(`${API_URL}/api/wizards/crm/search-contacts?q=${encodeURIComponent(searchQuery)}`, {
        headers: getAuthHeaders()
      });
      
      if (response.ok) {
        const data = await response.json();
        setSearchResults(data.contacts || []);
      }
    } catch (error) {
      toast.error('Error searching leads');
    } finally {
      setSearching(false);
    }
  };
  
  return (
    <div className="space-y-3">
      <FormField label={label} required>
        <div className="flex gap-2">
          <TextInput
            icon={Search}
            value={searchQuery}
            onChange={setSearchQuery}
            placeholder="Search by name, email, or phone..."
          />
          <Button variant="outline" onClick={searchLeads} disabled={searching}>
            {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Search'}
          </Button>
        </div>
      </FormField>
      
      {searchResults.length > 0 && (
        <div className="border border-white/10 rounded-xl overflow-hidden max-h-40 overflow-y-auto">
          {searchResults.map(lead => (
            <button
              key={lead.id}
              type="button"
              onClick={() => {
                onSelect(lead);
                setSearchResults([]);
              }}
              className={cn(
                "w-full flex items-center gap-3 p-3 hover:bg-white/5 transition-colors text-left",
                selectedLead?.id === lead.id && "bg-orange-500/10"
              )}
            >
              <User className="w-4 h-4 text-gray-500" />
              <div className="flex-1 min-w-0">
                <p className="text-sm text-white truncate">
                  {lead.firstName} {lead.lastName}
                </p>
                <p className="text-xs text-gray-500 truncate">
                  {lead.email || lead.phone}
                </p>
              </div>
              {selectedLead?.id === lead.id && (
                <CheckCircle className="w-4 h-4 text-orange-400" />
              )}
            </button>
          ))}
        </div>
      )}
      
      {selectedLead && (
        <div className="flex items-center gap-2 p-2 rounded-lg bg-orange-500/10 border border-orange-500/20">
          <User className="w-4 h-4 text-orange-400" />
          <span className="text-sm text-orange-400">
            Selected: {selectedLead.firstName} {selectedLead.lastName}
          </span>
          {selectedLead.phone && (
            <span className="text-xs text-gray-500 ml-auto">{selectedLead.phone}</span>
          )}
        </div>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════
// SEARCH LEAD WIZARD
// ═══════════════════════════════════════════════════════════════

export function SearchLeadWizard({ isOpen, onClose, onSuccess, initialData = {}, conversationId }) {
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState([]);
  const [selectedLead, setSelectedLead] = useState(null);
  const [leadDetails, setLeadDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [searchQuery, setSearchQuery] = useState(initialData.searchQuery || '');

  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      toast.error('Enter a search term');
      return;
    }
    
    setSearching(true);
    try {
      const response = await fetch(`${API_URL}/api/wizards/crm/search-contacts?q=${encodeURIComponent(searchQuery)}&limit=20`, {
        headers: getAuthHeaders()
      });
      
      if (response.ok) {
        const data = await response.json();
        setResults(data.contacts || []);
        if (data.contacts?.length === 0) {
          toast.info('No leads found');
        }
      }
    } catch (error) {
      toast.error('Error searching');
    } finally {
      setSearching(false);
    }
  };

  const loadLeadDetails = async (lead) => {
    setSelectedLead(lead);
    setLoadingDetails(true);
    
    try {
      const response = await fetch(
        `${API_URL}/api/wizards/crm/contact/${lead.id}`,
        { headers: getAuthHeaders() }
      );
      
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setLeadDetails(data.contact);
        } else {
          setLeadDetails(lead);
          toast.error('Could not load full details');
        }
      } else {
        setLeadDetails(lead);
      }
    } catch (error) {
      console.error('Failed to load lead details:', error);
      setLeadDetails(lead);
    } finally {
      setLoadingDetails(false);
    }
  };

  const closeDetails = () => {
    setSelectedLead(null);
    setLeadDetails(null);
  };

  const openInCRM = () => {
    if (leadDetails?.id && leadDetails?.locationId) {
      // One Force Live CRM URL - connects to client's sub-account
      const crmUrl = `https://app.oneforcelive.io/v2/location/${leadDetails.locationId}/contacts/detail/${leadDetails.id}`;
      window.open(crmUrl, '_blank');
    } else if (leadDetails?.id) {
      toast.info('Location ID not available - contact your admin for CRM access');
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value || 0);
  };

  const handleSelectForAction = () => {
    if (leadDetails) {
      toast.success(`Selected: ${leadDetails.firstName} ${leadDetails.lastName}`);
      onSuccess?.({ lead: leadDetails });
      closeDetails();
    }
  };

  return (
    <>
      <WizardModal
        isOpen={isOpen && !selectedLead}
        onClose={onClose}
        title="Search Lead"
        subtitle="Find and view lead details"
        icon={Search}
        iconColor="bg-orange-500/20"
        footer={
          <>
            <div className="text-sm text-gray-400">{results.length} results</div>
            <Button variant="outline" onClick={onClose}>Close</Button>
          </>
        }
      >
        <FormField label="Search Term" required>
          <div className="flex gap-2">
            <TextInput
              icon={Search}
              value={searchQuery}
              onChange={setSearchQuery}
              placeholder="Name, email, phone, or tag..."
              data-testid="search-lead-input"
            />
            <Button onClick={handleSearch} disabled={searching}>
              {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Search'}
            </Button>
          </div>
        </FormField>

        {results.length > 0 && (
          <div className="border border-white/10 rounded-xl overflow-hidden max-h-80 overflow-y-auto">
            {results.map(lead => (
              <button
                key={lead.id}
                type="button"
                onClick={() => loadLeadDetails(lead)}
                className="w-full flex items-center gap-3 p-4 hover:bg-orange-500/10 transition-colors text-left border-b border-white/5 last:border-0 group"
                data-testid={`lead-result-${lead.id}`}
              >
                <div className="w-10 h-10 rounded-full bg-orange-500/20 flex items-center justify-center flex-shrink-0">
                  <User className="w-5 h-5 text-orange-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-white">
                    {lead.firstName} {lead.lastName}
                  </p>
                  <p className="text-xs text-gray-500">{lead.email}</p>
                  <p className="text-xs text-gray-500">{lead.phone}</p>
                </div>
                <ChevronRight className="w-5 h-5 text-gray-500 group-hover:text-orange-400 transition-colors flex-shrink-0" />
              </button>
            ))}
          </div>
        )}
      </WizardModal>

      {/* Lead Details Modal */}
      {selectedLead && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-start justify-center p-4 pt-24 pb-8 overflow-y-auto" onClick={closeDetails}>
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            onClick={e => e.stopPropagation()}
            className="bg-[#12121a] border border-orange-500/30 rounded-2xl max-w-2xl w-full max-h-[calc(100vh-8rem)] overflow-hidden flex flex-col my-auto"
            data-testid="lead-details-modal"
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-orange-900/50 to-amber-900/50 p-5 border-b border-white/10">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 rounded-full bg-gradient-to-br from-orange-500 to-amber-500 flex items-center justify-center text-2xl font-bold text-white">
                    {leadDetails?.firstName?.[0] || '?'}{leadDetails?.lastName?.[0] || ''}
                  </div>
                  <div>
                    <h2 className="font-heading text-xl font-bold text-white">
                      {leadDetails?.firstName || ''} {leadDetails?.lastName || ''}
                    </h2>
                    <p className="text-sm text-gray-400">
                      {leadDetails?.companyName || 'Individual Lead'}
                    </p>
                    {leadDetails?.type && (
                      <span className="inline-block mt-1 px-2 py-0.5 rounded-full bg-orange-500/20 text-orange-400 text-xs capitalize">
                        {leadDetails.type}
                      </span>
                    )}
                  </div>
                </div>
                <button onClick={closeDetails} className="text-gray-400 hover:text-white p-2 hover:bg-white/10 rounded-lg transition-colors">
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>

            {loadingDetails ? (
              <div className="p-8 text-center flex-1">
                <Loader2 className="w-8 h-8 mx-auto text-orange-400 animate-spin mb-3" />
                <p className="text-gray-400">Loading lead details...</p>
              </div>
            ) : (
              <div className="p-5 overflow-y-auto flex-1">
                {/* Lead Info Section */}
                <div className="grid md:grid-cols-2 gap-4 mb-6">
                  {/* Primary Info */}
                  <div className="bg-white/5 rounded-xl p-4 border border-white/10">
                    <h3 className="font-semibold text-white mb-3 flex items-center gap-2">
                      <User className="w-4 h-4 text-orange-400" />
                      Contact Information
                    </h3>
                    <div className="space-y-3">
                      {leadDetails?.email && (
                        <div className="flex items-center gap-3">
                          <Mail className="w-4 h-4 text-gray-400 flex-shrink-0" />
                          <a href={`mailto:${leadDetails.email}`} className="text-orange-400 hover:underline text-sm truncate">
                            {leadDetails.email}
                          </a>
                        </div>
                      )}
                      {leadDetails?.phone && (
                        <div className="flex items-center gap-3">
                          <Phone className="w-4 h-4 text-gray-400 flex-shrink-0" />
                          <a href={`tel:${leadDetails.phone}`} className="text-orange-400 hover:underline text-sm">
                            {leadDetails.phone}
                          </a>
                        </div>
                      )}
                      {leadDetails?.website && (
                        <div className="flex items-center gap-3">
                          <Globe className="w-4 h-4 text-gray-400 flex-shrink-0" />
                          <a href={leadDetails.website} target="_blank" rel="noopener noreferrer" className="text-orange-400 hover:underline text-sm truncate">
                            {leadDetails.website}
                          </a>
                        </div>
                      )}
                      {(leadDetails?.address1 || leadDetails?.city) && (
                        <div className="flex items-start gap-3">
                          <MapPin className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                          <span className="text-gray-300 text-sm">
                            {leadDetails.address1}
                            {leadDetails.city && `, ${leadDetails.city}`}
                            {leadDetails.state && `, ${leadDetails.state}`}
                            {leadDetails.postalCode && ` ${leadDetails.postalCode}`}
                          </span>
                        </div>
                      )}
                      {!leadDetails?.email && !leadDetails?.phone && (
                        <p className="text-gray-500 text-sm">No contact information available</p>
                      )}
                    </div>
                  </div>

                  {/* Pipeline/Stage Info */}
                  <div className="bg-white/5 rounded-xl p-4 border border-white/10">
                    <h3 className="font-semibold text-white mb-3 flex items-center gap-2">
                      <Briefcase className="w-4 h-4 text-amber-400" />
                      Pipeline Status
                    </h3>
                    <div className="space-y-3">
                      {leadDetails?.pipelineStage ? (
                        <>
                          <div className="flex items-center justify-between">
                            <span className="text-gray-400 text-sm">Stage:</span>
                            <span className="px-3 py-1 rounded-full bg-amber-500/20 text-amber-400 text-sm font-medium">
                              {leadDetails.pipelineStage}
                            </span>
                          </div>
                          {leadDetails?.pipelineName && (
                            <div className="flex items-center justify-between">
                              <span className="text-gray-400 text-sm">Pipeline:</span>
                              <span className="text-white text-sm">{leadDetails.pipelineName}</span>
                            </div>
                          )}
                          {leadDetails?.totalOpportunityValue > 0 && (
                            <div className="flex items-center justify-between">
                              <span className="text-gray-400 text-sm">Total Value:</span>
                              <span className="text-green-400 font-medium text-sm">
                                {formatCurrency(leadDetails.totalOpportunityValue)}
                              </span>
                            </div>
                          )}
                        </>
                      ) : (
                        <p className="text-gray-500 text-sm">No pipeline assigned</p>
                      )}
                      
                      {/* Tags */}
                      {leadDetails?.tags && leadDetails.tags.length > 0 && (
                        <div className="pt-2 border-t border-white/10">
                          <span className="text-gray-400 text-sm block mb-2">Tags:</span>
                          <div className="flex flex-wrap gap-1">
                            {leadDetails.tags.slice(0, 6).map((tag, i) => (
                              <span key={i} className="px-2 py-0.5 rounded bg-orange-500/20 text-orange-400 text-xs">
                                {tag}
                              </span>
                            ))}
                            {leadDetails.tags.length > 6 && (
                              <span className="text-gray-500 text-xs">+{leadDetails.tags.length - 6} more</span>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Opportunities Section */}
                {leadDetails?.opportunities && leadDetails.opportunities.length > 0 && (
                  <div className="bg-white/5 rounded-xl p-4 border border-white/10 mb-6">
                    <h3 className="font-semibold text-white mb-3 flex items-center gap-2">
                      <DollarSign className="w-4 h-4 text-green-400" />
                      Opportunities ({leadDetails.opportunities.length})
                    </h3>
                    <div className="space-y-2 max-h-40 overflow-y-auto">
                      {leadDetails.opportunities.map((opp, i) => (
                        <div key={i} className="flex items-center justify-between p-2 rounded-lg bg-white/5 border border-white/5">
                          <div className="flex-1 min-w-0">
                            <p className="text-white text-sm font-medium truncate">{opp.name}</p>
                            <p className="text-xs text-gray-400">{opp.pipelineName} → {opp.stageName}</p>
                          </div>
                          <div className="text-right ml-3">
                            <p className="text-green-400 font-medium text-sm">{formatCurrency(opp.monetaryValue)}</p>
                            <span className={cn(
                              "text-xs px-2 py-0.5 rounded-full",
                              opp.status === 'open' ? 'bg-blue-500/20 text-blue-400' :
                              opp.status === 'won' ? 'bg-green-500/20 text-green-400' :
                              opp.status === 'lost' ? 'bg-red-500/20 text-red-400' :
                              'bg-gray-500/20 text-gray-400'
                            )}>
                              {opp.status}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Custom Fields */}
                {leadDetails?.customFields && Object.keys(leadDetails.customFields).length > 0 && (
                  <div className="bg-white/5 rounded-xl p-4 border border-white/10 mb-6">
                    <h3 className="font-semibold text-white mb-3 flex items-center gap-2">
                      <FileText className="w-4 h-4 text-purple-400" />
                      Custom Fields
                    </h3>
                    <div className="grid md:grid-cols-2 gap-3 max-h-32 overflow-y-auto">
                      {Object.entries(leadDetails.customFields).slice(0, 10).map(([key, value]) => (
                        <div key={key} className="flex justify-between items-start text-sm">
                          <span className="text-gray-400 capitalize">{key.replace(/_/g, ' ')}:</span>
                          <span className="text-white text-right ml-2 truncate max-w-[150px]">{String(value) || '-'}</span>
                        </div>
                      ))}
                    </div>
                    {Object.keys(leadDetails.customFields).length > 10 && (
                      <p className="text-xs text-gray-500 mt-2">+{Object.keys(leadDetails.customFields).length - 10} more fields</p>
                    )}
                  </div>
                )}

                {/* Activity/Timeline */}
                <div className="bg-white/5 rounded-xl p-4 border border-white/10">
                  <h3 className="font-semibold text-white mb-3 flex items-center gap-2">
                    <Clock className="w-4 h-4 text-green-400" />
                    Activity
                  </h3>
                  <div className="space-y-2 text-sm">
                    {leadDetails?.dateAdded && (
                      <div className="flex justify-between">
                        <span className="text-gray-400">Added:</span>
                        <span className="text-white">{new Date(leadDetails.dateAdded).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}</span>
                      </div>
                    )}
                    {leadDetails?.dateUpdated && (
                      <div className="flex justify-between">
                        <span className="text-gray-400">Last Updated:</span>
                        <span className="text-white">{new Date(leadDetails.dateUpdated).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}</span>
                      </div>
                    )}
                    {leadDetails?.source && (
                      <div className="flex justify-between">
                        <span className="text-gray-400">Source:</span>
                        <span className="text-white">{leadDetails.source}</span>
                      </div>
                    )}
                    {leadDetails?.id && (
                      <div className="flex justify-between">
                        <span className="text-gray-400">Lead ID:</span>
                        <span className="text-gray-500 font-mono text-xs">{leadDetails.id}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Footer Actions */}
            <div className="p-4 border-t border-white/10 bg-black/30">
              <div className="flex gap-3">
                <button
                  onClick={openInCRM}
                  disabled={!leadDetails?.locationId}
                  className="flex-1 py-3 px-4 rounded-xl bg-gradient-to-r from-orange-500 to-amber-500 text-white font-medium hover:opacity-90 transition-opacity flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="open-in-crm-btn"
                >
                  <ExternalLink className="w-4 h-4" />
                  Open in CRM
                </button>
                <button
                  onClick={handleSelectForAction}
                  className="py-3 px-6 rounded-xl border border-orange-500/30 text-orange-400 hover:bg-orange-500/10 transition-colors"
                >
                  Select Lead
                </button>
                <button
                  onClick={closeDetails}
                  className="py-3 px-6 rounded-xl border border-white/20 text-white hover:bg-white/10 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </>
  );
}

// ═══════════════════════════════════════════════════════════════
// CALL LEAD WIZARD
// ═══════════════════════════════════════════════════════════════

export function CallLeadWizard({ isOpen, onClose, onSuccess, initialData = {}, conversationId }) {
  const [selectedLead, setSelectedLead] = useState(null);
  const [calling, setCalling] = useState(false);

  const handleCall = async () => {
    if (!selectedLead) {
      toast.error('Please select a lead');
      return;
    }
    
    if (!selectedLead.phone) {
      toast.error('This lead has no phone number');
      return;
    }
    
    setCalling(true);
    try {
      // Log the call attempt
      const response = await fetch(`${API_URL}/api/wizards/lead/call`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          contact_id: selectedLead.id,
          phone: selectedLead.phone,
          conversation_id: conversationId
        })
      });
      
      // Initiate actual call via tel: protocol
      window.location.href = `tel:${selectedLead.phone}`;
      
      toast.success(`Calling ${selectedLead.firstName}...`);
      onSuccess?.({ lead: selectedLead });
      onClose();
    } catch (error) {
      // Still try to make the call even if logging fails
      window.location.href = `tel:${selectedLead.phone}`;
      toast.success(`Calling ${selectedLead.firstName}...`);
      onClose();
    } finally {
      setCalling(false);
    }
  };

  return (
    <WizardModal
      isOpen={isOpen}
      onClose={onClose}
      title="Call Lead"
      subtitle="Call a lead directly"
      icon={Phone}
      iconColor="bg-orange-500/20"
      footer={
        <>
          <div />
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button onClick={handleCall} disabled={calling || !selectedLead} className="bg-green-600 hover:bg-green-700">
              {calling ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Phone className="w-4 h-4 mr-2" />}
              Call Now
            </Button>
          </div>
        </>
      }
    >
      <LeadSelector
        selectedLead={selectedLead}
        onSelect={setSelectedLead}
      />

      {selectedLead && selectedLead.phone && (
        <div className="mt-4 p-4 rounded-xl bg-green-500/10 border border-green-500/20">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-green-500/20 flex items-center justify-center">
              <Phone className="w-6 h-6 text-green-400" />
            </div>
            <div>
              <p className="text-white font-medium">{selectedLead.firstName} {selectedLead.lastName}</p>
              <p className="text-green-400 text-lg font-mono">{selectedLead.phone}</p>
            </div>
          </div>
        </div>
      )}
    </WizardModal>
  );
}

// ═══════════════════════════════════════════════════════════════
// TEXT LEAD WIZARD
// ═══════════════════════════════════════════════════════════════

export function TextLeadWizard({ isOpen, onClose, onSuccess, initialData = {}, conversationId }) {
  const [selectedLead, setSelectedLead] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState(initialData.message || '');

  const handleSubmit = async () => {
    if (!selectedLead) {
      toast.error('Please select a lead');
      return;
    }
    if (!selectedLead.phone) {
      toast.error('This lead has no phone number');
      return;
    }
    if (!message.trim()) {
      toast.error('Please enter a message');
      return;
    }
    
    setSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/wizards/crm/sms`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          contact_id: selectedLead.id,
          phone: selectedLead.phone,
          email: selectedLead.email,
          message: message,
          conversation_id: conversationId
        })
      });
      
      const result = await response.json();
      
      if (response.ok && result.success) {
        toast.success(`SMS sent to ${selectedLead.firstName}!`);
        onSuccess?.(result);
        onClose();
      } else {
        toast.error(result.error || 'Failed to send SMS');
      }
    } catch (error) {
      toast.error('Error sending SMS');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <WizardModal
      isOpen={isOpen}
      onClose={onClose}
      title="Text Lead"
      subtitle="Send SMS to a lead"
      icon={MessageSquare}
      iconColor="bg-orange-500/20"
      footer={
        <>
          <div className="text-xs text-gray-500">{message.length}/1600</div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button onClick={handleSubmit} disabled={submitting}>
              {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <MessageSquare className="w-4 h-4 mr-2" />}
              Send SMS
            </Button>
          </div>
        </>
      }
    >
      <LeadSelector selectedLead={selectedLead} onSelect={setSelectedLead} />

      <FormField label="Message" required>
        <TextArea
          value={message}
          onChange={setMessage}
          placeholder="Type your message..."
          rows={4}
          maxLength={1600}
        />
      </FormField>
    </WizardModal>
  );
}

// ═══════════════════════════════════════════════════════════════
// EMAIL LEAD WIZARD
// ═══════════════════════════════════════════════════════════════

export function EmailLeadWizard({ isOpen, onClose, onSuccess, initialData = {}, conversationId }) {
  const [selectedLead, setSelectedLead] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    subject: initialData.subject || '',
    body: initialData.body || '',
  });

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    if (!selectedLead) {
      toast.error('Please select a lead');
      return;
    }
    if (!selectedLead.email) {
      toast.error('This lead has no email address');
      return;
    }
    if (!formData.subject.trim()) {
      toast.error('Please enter a subject');
      return;
    }
    if (!formData.body.trim()) {
      toast.error('Please enter a message');
      return;
    }
    
    setSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/wizards/crm/email`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          contact_id: selectedLead.id,
          email: selectedLead.email,
          phone: selectedLead.phone,
          subject: formData.subject,
          body: formData.body,
          conversation_id: conversationId
        })
      });
      
      const result = await response.json();
      
      if (response.ok && result.success) {
        toast.success(`Email sent to ${selectedLead.firstName}!`);
        onSuccess?.(result);
        onClose();
      } else {
        toast.error(result.error || 'Failed to send email');
      }
    } catch (error) {
      toast.error('Error sending email');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <WizardModal
      isOpen={isOpen}
      onClose={onClose}
      title="Email Lead"
      subtitle="Send email to a lead"
      icon={Mail}
      iconColor="bg-orange-500/20"
      footer={
        <>
          <div />
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button onClick={handleSubmit} disabled={submitting}>
              {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Mail className="w-4 h-4 mr-2" />}
              Send Email
            </Button>
          </div>
        </>
      }
    >
      <LeadSelector selectedLead={selectedLead} onSelect={setSelectedLead} />

      <FormField label="Subject" required>
        <TextInput
          value={formData.subject}
          onChange={v => handleChange('subject', v)}
          placeholder="Email subject..."
        />
      </FormField>

      <FormField label="Message" required>
        <TextArea
          value={formData.body}
          onChange={v => handleChange('body', v)}
          placeholder="Email body..."
          rows={6}
        />
      </FormField>
    </WizardModal>
  );
}

// ═══════════════════════════════════════════════════════════════
// BOOK LEAD APPOINTMENT WIZARD
// ═══════════════════════════════════════════════════════════════

export function BookLeadAppointmentWizard({ isOpen, onClose, onSuccess, initialData = {}, conversationId }) {
  const [selectedLead, setSelectedLead] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [calendars, setCalendars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    calendarId: initialData.calendarId || '',
    title: initialData.title || '',
    startTime: initialData.startTime || '',
    duration: initialData.duration || '30',
    notes: initialData.notes || '',
  });

  useEffect(() => {
    const fetchCalendars = async () => {
      try {
        const response = await fetch(`${API_URL}/api/wizards/crm/calendars`, {
          headers: getAuthHeaders()
        });
        if (response.ok) {
          const data = await response.json();
          setCalendars(data.calendars || []);
        }
      } catch (error) {
        console.error('Error fetching calendars:', error);
      } finally {
        setLoading(false);
      }
    };
    if (isOpen) fetchCalendars();
  }, [isOpen]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    if (!selectedLead) {
      toast.error('Please select a lead');
      return;
    }
    if (!formData.calendarId) {
      toast.error('Please select a calendar');
      return;
    }
    if (!formData.startTime) {
      toast.error('Please select a date/time');
      return;
    }
    
    setSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/wizards/crm/appointment`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          contact_id: selectedLead.id,
          calendar_id: formData.calendarId,
          title: formData.title || `Appointment with ${selectedLead.firstName}`,
          start_time: formData.startTime,
          duration_minutes: parseInt(formData.duration),
          notes: formData.notes,
          conversation_id: conversationId
        })
      });
      
      const result = await response.json();
      
      if (response.ok && result.success) {
        toast.success(`Appointment booked with ${selectedLead.firstName}!`);
        onSuccess?.(result);
        onClose();
      } else {
        toast.error(result.error || 'Failed to book appointment');
      }
    } catch (error) {
      toast.error('Error booking appointment');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <WizardModal
      isOpen={isOpen}
      onClose={onClose}
      title="Book Appointment"
      subtitle="Schedule appointment with lead"
      icon={Calendar}
      iconColor="bg-orange-500/20"
      footer={
        <>
          <div />
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button onClick={handleSubmit} disabled={submitting || loading}>
              {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Calendar className="w-4 h-4 mr-2" />}
              Book Appointment
            </Button>
          </div>
        </>
      }
    >
      <LeadSelector selectedLead={selectedLead} onSelect={setSelectedLead} />

      <FormField label="Calendar" required>
        <SelectInput
          value={formData.calendarId}
          onChange={v => handleChange('calendarId', v)}
          placeholder="Select calendar"
          options={calendars.map(c => ({ value: c.id, label: c.name }))}
        />
      </FormField>

      <FormField label="Title">
        <TextInput
          value={formData.title}
          onChange={v => handleChange('title', v)}
          placeholder="Appointment title..."
        />
      </FormField>

      <div className="grid grid-cols-2 gap-4">
        <FormField label="Date & Time" required>
          <TextInput
            type="datetime-local"
            value={formData.startTime}
            onChange={v => handleChange('startTime', v)}
          />
        </FormField>
        <FormField label="Duration">
          <SelectInput
            value={formData.duration}
            onChange={v => handleChange('duration', v)}
            options={[
              { value: '15', label: '15 min' },
              { value: '30', label: '30 min' },
              { value: '45', label: '45 min' },
              { value: '60', label: '1 hour' },
              { value: '90', label: '1.5 hours' },
            ]}
          />
        </FormField>
      </div>

      <FormField label="Notes">
        <TextArea
          value={formData.notes}
          onChange={v => handleChange('notes', v)}
          placeholder="Appointment notes..."
          rows={2}
        />
      </FormField>
    </WizardModal>
  );
}

// ═══════════════════════════════════════════════════════════════
// SEND AGREEMENT WIZARD
// ═══════════════════════════════════════════════════════════════

export function SendLeadAgreementWizard({ isOpen, onClose, onSuccess, initialData = {}, conversationId }) {
  const [selectedLead, setSelectedLead] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    agreementType: initialData.agreementType || 'service_agreement',
    title: initialData.title || '',
    description: initialData.description || '',
    requireSignature: initialData.requireSignature ?? true,
  });

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    if (!selectedLead) {
      toast.error('Please select a lead');
      return;
    }
    if (!formData.title.trim()) {
      toast.error('Please enter an agreement title');
      return;
    }
    
    setSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/wizards/lead/agreement`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          contact_id: selectedLead.id,
          agreement_type: formData.agreementType,
          title: formData.title,
          description: formData.description,
          require_signature: formData.requireSignature,
          conversation_id: conversationId
        })
      });
      
      const result = await response.json();
      
      if (response.ok && result.success) {
        toast.success(`Agreement sent to ${selectedLead.firstName}!`);
        onSuccess?.(result);
        onClose();
      } else {
        toast.error(result.error || 'Failed to send agreement');
      }
    } catch (error) {
      toast.error('Error sending agreement');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <WizardModal
      isOpen={isOpen}
      onClose={onClose}
      title="Send Agreement"
      subtitle="Send agreement to lead"
      icon={FileText}
      iconColor="bg-orange-500/20"
      footer={
        <>
          <div />
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button onClick={handleSubmit} disabled={submitting}>
              {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileText className="w-4 h-4 mr-2" />}
              Send Agreement
            </Button>
          </div>
        </>
      }
    >
      <LeadSelector selectedLead={selectedLead} onSelect={setSelectedLead} />

      <FormField label="Agreement Type" required>
        <SelectInput
          value={formData.agreementType}
          onChange={v => handleChange('agreementType', v)}
          options={[
            { value: 'service_agreement', label: 'Service Agreement' },
            { value: 'nda', label: 'Non-Disclosure Agreement' },
            { value: 'contract', label: 'Contract' },
            { value: 'terms_of_service', label: 'Terms of Service' },
            { value: 'custom', label: 'Custom Agreement' },
          ]}
        />
      </FormField>

      <FormField label="Title" required>
        <TextInput
          value={formData.title}
          onChange={v => handleChange('title', v)}
          placeholder="Agreement title..."
        />
      </FormField>

      <FormField label="Description">
        <TextArea
          value={formData.description}
          onChange={v => handleChange('description', v)}
          placeholder="Agreement details..."
          rows={3}
        />
      </FormField>

      <label className="flex items-center gap-3 p-3 rounded-xl bg-white/5 border border-white/10 cursor-pointer">
        <input
          type="checkbox"
          checked={formData.requireSignature}
          onChange={e => handleChange('requireSignature', e.target.checked)}
          className="w-4 h-4 rounded"
        />
        <span className="text-sm text-white">Require electronic signature</span>
      </label>
    </WizardModal>
  );
}

// ═══════════════════════════════════════════════════════════════
// SEND ESTIMATE WIZARD
// ═══════════════════════════════════════════════════════════════

export function SendLeadEstimateWizard({ isOpen, onClose, onSuccess, initialData = {}, conversationId }) {
  const [selectedLead, setSelectedLead] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    title: initialData.title || '',
    items: initialData.items || [{ description: '', amount: '' }],
    validDays: initialData.validDays || '30',
    notes: initialData.notes || '',
  });

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const addItem = () => {
    handleChange('items', [...formData.items, { description: '', amount: '' }]);
  };

  const updateItem = (index, field, value) => {
    const newItems = [...formData.items];
    newItems[index] = { ...newItems[index], [field]: value };
    handleChange('items', newItems);
  };

  const removeItem = (index) => {
    handleChange('items', formData.items.filter((_, i) => i !== index));
  };

  const total = formData.items.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0);

  const handleSubmit = async () => {
    if (!selectedLead) {
      toast.error('Please select a lead');
      return;
    }
    if (!formData.title.trim()) {
      toast.error('Please enter a title');
      return;
    }
    
    setSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/wizards/crm/proposal`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          contact_id: selectedLead.id,
          title: formData.title,
          line_items: formData.items.map(i => ({
            name: i.description,
            quantity: 1,
            price: parseFloat(i.amount) || 0
          })),
          expiration_date: null,
          send_immediately: true,
          conversation_id: conversationId
        })
      });
      
      const result = await response.json();
      
      if (response.ok && result.success) {
        toast.success(`Estimate sent to ${selectedLead.firstName}!`);
        onSuccess?.(result);
        onClose();
      } else {
        toast.error(result.error || 'Failed to send estimate');
      }
    } catch (error) {
      toast.error('Error sending estimate');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <WizardModal
      isOpen={isOpen}
      onClose={onClose}
      title="Send Estimate"
      subtitle="Send estimate to lead"
      icon={Receipt}
      iconColor="bg-orange-500/20"
      footer={
        <>
          <div className="text-sm text-gray-400">
            Total: <span className="text-white font-semibold">${total.toFixed(2)}</span>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button onClick={handleSubmit} disabled={submitting}>
              {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Receipt className="w-4 h-4 mr-2" />}
              Send Estimate
            </Button>
          </div>
        </>
      }
    >
      <LeadSelector selectedLead={selectedLead} onSelect={setSelectedLead} />

      <FormField label="Estimate Title" required>
        <TextInput
          value={formData.title}
          onChange={v => handleChange('title', v)}
          placeholder="e.g., Website Development Estimate"
        />
      </FormField>

      <FormField label="Line Items">
        <div className="space-y-2">
          {formData.items.map((item, index) => (
            <div key={index} className="flex gap-2">
              <input
                type="text"
                value={item.description}
                onChange={e => updateItem(index, 'description', e.target.value)}
                placeholder="Description"
                className="flex-1 px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm"
              />
              <input
                type="number"
                value={item.amount}
                onChange={e => updateItem(index, 'amount', e.target.value)}
                placeholder="Amount"
                className="w-28 px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm"
              />
              {formData.items.length > 1 && (
                <button onClick={() => removeItem(index)} className="px-2 text-red-400 hover:text-red-300">✕</button>
              )}
            </div>
          ))}
          <button
            type="button"
            onClick={addItem}
            className="w-full py-2 border border-dashed border-white/20 rounded-lg text-sm text-gray-400 hover:bg-white/5"
          >
            + Add Item
          </button>
        </div>
      </FormField>

      <FormField label="Notes">
        <TextArea
          value={formData.notes}
          onChange={v => handleChange('notes', v)}
          placeholder="Additional notes..."
          rows={2}
        />
      </FormField>
    </WizardModal>
  );
}

// ═══════════════════════════════════════════════════════════════
// SEND INVOICE WIZARD (Lead focused)
// ═══════════════════════════════════════════════════════════════

export function SendLeadInvoiceWizard({ isOpen, onClose, onSuccess, initialData = {}, conversationId }) {
  const [selectedLead, setSelectedLead] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    items: initialData.items || [{ description: '', amount: '' }],
    dueDate: initialData.dueDate || '',
    notes: initialData.notes || '',
  });

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const addItem = () => {
    handleChange('items', [...formData.items, { description: '', amount: '' }]);
  };

  const updateItem = (index, field, value) => {
    const newItems = [...formData.items];
    newItems[index] = { ...newItems[index], [field]: value };
    handleChange('items', newItems);
  };

  const removeItem = (index) => {
    handleChange('items', formData.items.filter((_, i) => i !== index));
  };

  const total = formData.items.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0);

  const handleSubmit = async () => {
    if (!selectedLead) {
      toast.error('Please select a lead');
      return;
    }
    if (!formData.dueDate) {
      toast.error('Please select a due date');
      return;
    }
    
    setSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/wizards/crm/invoice`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          contact_id: selectedLead.id,
          auto_generate_number: true,
          line_items: formData.items.map(i => ({
            name: i.description,
            quantity: 1,
            price: parseFloat(i.amount) || 0
          })),
          due_date: formData.dueDate,
          send_immediately: true,
          conversation_id: conversationId
        })
      });
      
      const result = await response.json();
      
      if (response.ok && result.success) {
        toast.success(`Invoice sent to ${selectedLead.firstName}!`);
        onSuccess?.(result);
        onClose();
      } else {
        toast.error(result.error || 'Failed to send invoice');
      }
    } catch (error) {
      toast.error('Error sending invoice');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <WizardModal
      isOpen={isOpen}
      onClose={onClose}
      title="Send Invoice"
      subtitle="Send invoice to lead"
      icon={Receipt}
      iconColor="bg-orange-500/20"
      footer={
        <>
          <div className="text-sm text-gray-400">
            Total: <span className="text-white font-semibold">${total.toFixed(2)}</span>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button onClick={handleSubmit} disabled={submitting}>
              {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Receipt className="w-4 h-4 mr-2" />}
              Send Invoice
            </Button>
          </div>
        </>
      }
    >
      <LeadSelector selectedLead={selectedLead} onSelect={setSelectedLead} />

      <FormField label="Due Date" required>
        <TextInput
          type="date"
          value={formData.dueDate}
          onChange={v => handleChange('dueDate', v)}
        />
      </FormField>

      <FormField label="Line Items">
        <div className="space-y-2">
          {formData.items.map((item, index) => (
            <div key={index} className="flex gap-2">
              <input
                type="text"
                value={item.description}
                onChange={e => updateItem(index, 'description', e.target.value)}
                placeholder="Description"
                className="flex-1 px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm"
              />
              <input
                type="number"
                value={item.amount}
                onChange={e => updateItem(index, 'amount', e.target.value)}
                placeholder="Amount"
                className="w-28 px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm"
              />
              {formData.items.length > 1 && (
                <button onClick={() => removeItem(index)} className="px-2 text-red-400 hover:text-red-300">✕</button>
              )}
            </div>
          ))}
          <button
            type="button"
            onClick={addItem}
            className="w-full py-2 border border-dashed border-white/20 rounded-lg text-sm text-gray-400 hover:bg-white/5"
          >
            + Add Item
          </button>
        </div>
      </FormField>
    </WizardModal>
  );
}

// ═══════════════════════════════════════════════════════════════
// SEND PAYMENT LINK WIZARD (Lead focused)
// ═══════════════════════════════════════════════════════════════

export function SendLeadPaymentLinkWizard({ isOpen, onClose, onSuccess, initialData = {}, conversationId }) {
  const [selectedLead, setSelectedLead] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    amount: initialData.amount || '',
    description: initialData.description || '',
    sendMethod: initialData.sendMethod || 'both',
  });

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    if (!selectedLead) {
      toast.error('Please select a lead');
      return;
    }
    if (!formData.amount) {
      toast.error('Please enter an amount');
      return;
    }
    
    setSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/wizards/crm/payment-link`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          link_name: formData.description || `Payment from ${selectedLead.firstName}`,
          amount: parseFloat(formData.amount),
          payment_type: 'one_time',
          contact_id: selectedLead.id,
          send_link: true,
          send_method: formData.sendMethod,
          conversation_id: conversationId
        })
      });
      
      const result = await response.json();
      
      if (response.ok && result.success) {
        toast.success(`Payment link sent to ${selectedLead.firstName}!`);
        onSuccess?.(result);
        onClose();
      } else {
        toast.error(result.error || 'Failed to send payment link');
      }
    } catch (error) {
      toast.error('Error sending payment link');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <WizardModal
      isOpen={isOpen}
      onClose={onClose}
      title="Send Payment Link"
      subtitle="Send payment link to lead"
      icon={Link2}
      iconColor="bg-orange-500/20"
      footer={
        <>
          <div />
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button onClick={handleSubmit} disabled={submitting}>
              {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Link2 className="w-4 h-4 mr-2" />}
              Send Link
            </Button>
          </div>
        </>
      }
    >
      <LeadSelector selectedLead={selectedLead} onSelect={setSelectedLead} />

      <FormField label="Amount ($)" required>
        <TextInput
          type="number"
          value={formData.amount}
          onChange={v => handleChange('amount', v)}
          placeholder="0.00"
        />
      </FormField>

      <FormField label="Description">
        <TextInput
          value={formData.description}
          onChange={v => handleChange('description', v)}
          placeholder="What is this payment for?"
        />
      </FormField>

      <FormField label="Send Via">
        <SelectInput
          value={formData.sendMethod}
          onChange={v => handleChange('sendMethod', v)}
          options={[
            { value: 'email', label: 'Email only' },
            { value: 'sms', label: 'SMS only' },
            { value: 'both', label: 'Both Email & SMS' },
          ]}
        />
      </FormField>
    </WizardModal>
  );
}

// ═══════════════════════════════════════════════════════════════
// SEND SUBSCRIPTION WIZARD (Lead focused)
// ═══════════════════════════════════════════════════════════════

export function SendLeadSubscriptionWizard({ isOpen, onClose, onSuccess, initialData = {}, conversationId }) {
  const [selectedLead, setSelectedLead] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    amount: initialData.amount || '',
    frequency: initialData.frequency || 'monthly',
    description: initialData.description || '',
    trialDays: initialData.trialDays || '',
  });

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    if (!selectedLead) {
      toast.error('Please select a lead');
      return;
    }
    if (!formData.amount) {
      toast.error('Please enter an amount');
      return;
    }
    
    setSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/wizards/crm/payment-link`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          link_name: formData.description || `Subscription for ${selectedLead.firstName}`,
          amount: parseFloat(formData.amount),
          payment_type: 'recurring',
          billing_frequency: formData.frequency,
          trial_period: formData.trialDays ? parseInt(formData.trialDays) : undefined,
          contact_id: selectedLead.id,
          send_link: true,
          send_method: 'both',
          conversation_id: conversationId
        })
      });
      
      const result = await response.json();
      
      if (response.ok && result.success) {
        toast.success(`Subscription link sent to ${selectedLead.firstName}!`);
        onSuccess?.(result);
        onClose();
      } else {
        toast.error(result.error || 'Failed to send subscription');
      }
    } catch (error) {
      toast.error('Error sending subscription');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <WizardModal
      isOpen={isOpen}
      onClose={onClose}
      title="Send Subscription"
      subtitle="Set up recurring payment for lead"
      icon={CreditCard}
      iconColor="bg-orange-500/20"
      footer={
        <>
          <div />
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button onClick={handleSubmit} disabled={submitting}>
              {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CreditCard className="w-4 h-4 mr-2" />}
              Send Subscription
            </Button>
          </div>
        </>
      }
    >
      <LeadSelector selectedLead={selectedLead} onSelect={setSelectedLead} />

      <div className="grid grid-cols-2 gap-4">
        <FormField label="Amount ($)" required>
          <TextInput
            type="number"
            value={formData.amount}
            onChange={v => handleChange('amount', v)}
            placeholder="0.00"
          />
        </FormField>
        <FormField label="Billing Frequency" required>
          <SelectInput
            value={formData.frequency}
            onChange={v => handleChange('frequency', v)}
            options={[
              { value: 'weekly', label: 'Weekly' },
              { value: 'monthly', label: 'Monthly' },
              { value: 'quarterly', label: 'Quarterly' },
              { value: 'yearly', label: 'Yearly' },
            ]}
          />
        </FormField>
      </div>

      <FormField label="Description">
        <TextInput
          value={formData.description}
          onChange={v => handleChange('description', v)}
          placeholder="e.g., Monthly Service Fee"
        />
      </FormField>

      <FormField label="Trial Period (days)" hint="Leave empty for no trial">
        <TextInput
          type="number"
          value={formData.trialDays}
          onChange={v => handleChange('trialDays', v)}
          placeholder="0"
        />
      </FormField>
    </WizardModal>
  );
}

// ═══════════════════════════════════════════════════════════════
// CREATE LEAD WIZARD
// ═══════════════════════════════════════════════════════════════

export function CreateLeadWizard({ isOpen, onClose, onSuccess, initialData = {}, conversationId }) {
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    firstName: initialData.firstName || '',
    lastName: initialData.lastName || '',
    email: initialData.email || '',
    phone: initialData.phone || '',
    company: initialData.company || '',
    source: initialData.source || '',
    tags: initialData.tags || '',
  });

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    if (!formData.firstName.trim()) {
      toast.error('First name is required');
      return;
    }
    if (!formData.email.trim() && !formData.phone.trim()) {
      toast.error('Email or phone is required');
      return;
    }
    
    setSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/wizards/crm/contact`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          first_name: formData.firstName,
          last_name: formData.lastName,
          email: formData.email || undefined,
          phone: formData.phone || undefined,
          company_name: formData.company || undefined,
          source: formData.source || undefined,
          tags: formData.tags ? formData.tags.split(',').map(t => t.trim()) : undefined,
          conversation_id: conversationId
        })
      });
      
      const result = await response.json();
      
      if (response.ok && result.success) {
        toast.success(`Lead "${formData.firstName} ${formData.lastName}" created!`);
        onSuccess?.(result);
        onClose();
      } else {
        toast.error(result.error || 'Failed to create lead');
      }
    } catch (error) {
      toast.error('Error creating lead');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <WizardModal
      isOpen={isOpen}
      onClose={onClose}
      title="Create Lead"
      subtitle="Add a new lead to your CRM"
      icon={UserPlus}
      iconColor="bg-orange-500/20"
      footer={
        <>
          <div />
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button onClick={handleSubmit} disabled={submitting}>
              {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <UserPlus className="w-4 h-4 mr-2" />}
              Create Lead
            </Button>
          </div>
        </>
      }
    >
      <div className="grid grid-cols-2 gap-4">
        <FormField label="First Name" required>
          <TextInput
            icon={User}
            value={formData.firstName}
            onChange={v => handleChange('firstName', v)}
            placeholder="John"
          />
        </FormField>
        <FormField label="Last Name">
          <TextInput
            value={formData.lastName}
            onChange={v => handleChange('lastName', v)}
            placeholder="Smith"
          />
        </FormField>
      </div>

      <FormField label="Email">
        <TextInput
          icon={Mail}
          type="email"
          value={formData.email}
          onChange={v => handleChange('email', v)}
          placeholder="john@example.com"
        />
      </FormField>

      <FormField label="Phone">
        <TextInput
          icon={Phone}
          type="tel"
          value={formData.phone}
          onChange={v => handleChange('phone', v)}
          placeholder="(555) 123-4567"
        />
      </FormField>

      <FormField label="Company">
        <TextInput
          value={formData.company}
          onChange={v => handleChange('company', v)}
          placeholder="Company name"
        />
      </FormField>

      <div className="grid grid-cols-2 gap-4">
        <FormField label="Lead Source">
          <SelectInput
            value={formData.source}
            onChange={v => handleChange('source', v)}
            placeholder="Select source"
            options={[
              { value: 'website', label: 'Website' },
              { value: 'referral', label: 'Referral' },
              { value: 'social_media', label: 'Social Media' },
              { value: 'advertising', label: 'Advertising' },
              { value: 'cold_call', label: 'Cold Call' },
              { value: 'event', label: 'Event' },
              { value: 'other', label: 'Other' },
            ]}
          />
        </FormField>
        <FormField label="Tags" hint="Comma separated">
          <TextInput
            value={formData.tags}
            onChange={v => handleChange('tags', v)}
            placeholder="hot-lead, enterprise"
          />
        </FormField>
      </div>
    </WizardModal>
  );
}

// ═══════════════════════════════════════════════════════════════
// SEARCH LEAD PIPELINES WIZARD
// ═══════════════════════════════════════════════════════════════

export function SearchLeadPipelinesWizard({ isOpen, onClose, onSuccess, initialData = {}, conversationId }) {
  const [loading, setLoading] = useState(true);
  const [pipelines, setPipelines] = useState([]);
  const [selectedPipeline, setSelectedPipeline] = useState(null);
  const [selectedStage, setSelectedStage] = useState(null);
  const [leads, setLeads] = useState([]);
  const [loadingLeads, setLoadingLeads] = useState(false);

  useEffect(() => {
    const fetchPipelines = async () => {
      try {
        const response = await fetch(`${API_URL}/api/wizards/crm/pipelines`, {
          headers: getAuthHeaders()
        });
        if (response.ok) {
          const data = await response.json();
          setPipelines(data.pipelines || []);
        }
      } catch (error) {
        console.error('Error fetching pipelines:', error);
      } finally {
        setLoading(false);
      }
    };
    if (isOpen) fetchPipelines();
  }, [isOpen]);

  const fetchLeadsByStage = async (pipelineId, stageId) => {
    setLoadingLeads(true);
    try {
      const response = await fetch(`${API_URL}/api/wizards/crm/opportunities?pipeline_id=${pipelineId}&stage_id=${stageId}`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setLeads(data.opportunities || []);
      }
    } catch (error) {
      console.error('Error fetching leads:', error);
    } finally {
      setLoadingLeads(false);
    }
  };

  const handleSelectPipeline = (pipeline) => {
    setSelectedPipeline(pipeline);
    setSelectedStage(null);
    setLeads([]);
  };

  const handleSelectStage = (stage) => {
    setSelectedStage(stage);
    if (selectedPipeline) {
      fetchLeadsByStage(selectedPipeline.id, stage.id);
    }
  };

  return (
    <WizardModal
      isOpen={isOpen}
      onClose={onClose}
      title="Search Lead Pipelines"
      subtitle="View leads by pipeline stage"
      icon={GitBranch}
      iconColor="bg-orange-500/20"
      size="large"
      footer={
        <>
          <div className="text-sm text-gray-400">{leads.length} leads in stage</div>
          <Button variant="outline" onClick={onClose}>Close</Button>
        </>
      }
    >
      {loading ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin text-primary" />
        </div>
      ) : (
        <div className="space-y-4">
          {/* Pipeline Selection */}
          <FormField label="Select Pipeline">
            <div className="grid grid-cols-2 gap-2">
              {pipelines.map(pipeline => (
                <button
                  key={pipeline.id}
                  type="button"
                  onClick={() => handleSelectPipeline(pipeline)}
                  className={cn(
                    "p-3 rounded-xl border text-left transition-colors",
                    selectedPipeline?.id === pipeline.id
                      ? "bg-orange-500/10 border-orange-500/30"
                      : "bg-white/5 border-white/10 hover:bg-white/10"
                  )}
                >
                  <div className="flex items-center gap-2">
                    <Briefcase className="w-4 h-4 text-gray-500" />
                    <span className="text-sm text-white">{pipeline.name}</span>
                  </div>
                </button>
              ))}
            </div>
          </FormField>

          {/* Stage Selection */}
          {selectedPipeline && selectedPipeline.stages && (
            <FormField label="Select Stage">
              <div className="flex gap-2 overflow-x-auto pb-2">
                {selectedPipeline.stages.map((stage, index) => (
                  <button
                    key={stage.id || index}
                    type="button"
                    onClick={() => handleSelectStage(stage)}
                    className={cn(
                      "flex-shrink-0 px-4 py-2 rounded-lg border transition-colors",
                      selectedStage?.id === stage.id || selectedStage?.name === stage.name
                        ? "bg-orange-500/20 border-orange-500/50 text-orange-400"
                        : "bg-white/5 border-white/10 text-gray-400 hover:bg-white/10"
                    )}
                  >
                    <span className="text-sm whitespace-nowrap">{stage.name}</span>
                  </button>
                ))}
              </div>
            </FormField>
          )}

          {/* Leads List */}
          {selectedStage && (
            <div className="border border-white/10 rounded-xl overflow-hidden">
              {loadingLeads ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-5 h-5 animate-spin text-primary" />
                </div>
              ) : leads.length > 0 ? (
                <div className="max-h-64 overflow-y-auto">
                  {leads.map(lead => (
                    <div
                      key={lead.id}
                      className="flex items-center gap-3 p-3 border-b border-white/5 last:border-0 hover:bg-white/5"
                    >
                      <div className="w-10 h-10 rounded-full bg-orange-500/20 flex items-center justify-center">
                        <User className="w-5 h-5 text-orange-400" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-white truncate">{lead.name || lead.contact_name}</p>
                        <p className="text-xs text-gray-500">{lead.email || lead.contact_email}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-medium text-green-400">
                          ${(lead.value || lead.monetary_value || 0).toLocaleString()}
                        </p>
                        <p className="text-xs text-gray-500">{lead.status}</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <p className="text-gray-400">No leads in this stage</p>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </WizardModal>
  );
}
